import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CheckCircleIcon,
  ClockCounterClockwiseIcon,
  CoinIcon,
  HourglassMediumIcon,
  ShieldCheckIcon,
  SignatureIcon,
  SpinnerIcon,
  WalletIcon,
  XCircleIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { KeyValue } from '@/components/ui/key-value';

import {
  createOrderDraft,
  getOrderParams,
  submitOrder,
  uploadClobCreds,
  type OrderParamsResponse,
} from '../api';
import { useAuth } from '../auth';
import { useT } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';
import { tryGetTelegram } from '../telegram';
import { deriveClobCreds, getSigner } from '../wallet';

type Phase =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; params: OrderParamsResponse }
  | { kind: 'signing' }
  | {
      kind: 'submitted';
      orderId: string;
      status: string;
      errorMessage?: string | null;
    };

const fmtUsd = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;

export default function SignOrder() {
  const [search, setSearch] = useSearchParams();
  const orderId = search.get('orderId');
  const auth = useAuth();
  const t = useT();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useTelegramBackButton();
  useNavStackEntry({ label: t('sign_order.breadcrumb'), mode: 'replace' });

  // Buy intent forwarded by the bot deeplink. Used to silently recreate the
  // draft when the in-memory one has expired (TTL) or was created on a
  // different API replica — without that fallback the user just sees a
  // permanent "Draft … not found or expired" alert and is stuck.
  const intent = useMemo((): {
    slug: string;
    outcome: string;
    side: 'BUY' | 'SELL';
    amount: number;
  } | null => {
    const slug = search.get('slug');
    const outcome = search.get('outcome');
    const sideRaw = search.get('side');
    const amountRaw = search.get('amount');
    const amount = amountRaw != null ? Number(amountRaw) : NaN;
    const side: 'BUY' | 'SELL' | null =
      sideRaw === 'BUY' || sideRaw === 'SELL' ? sideRaw : null;
    if (!slug || !outcome || !side || !Number.isFinite(amount) || amount <= 0) {
      return null;
    }
    return { slug, outcome, side, amount };
  }, [search]);

  useEffect(() => {
    if (auth.state !== 'ready' || !orderId) return;
    let cancelled = false;
    (async () => {
      const isExpired = (err: unknown) => {
        const msg = (err as Error)?.message ?? '';
        return /not found or expired/i.test(msg) || /^404\b/.test(msg);
      };

      try {
        const params = await getOrderParams(orderId);
        if (!cancelled) setPhase({ kind: 'ready', params });
        return;
      } catch (err) {
        if (cancelled) return;
        if (!isExpired(err) || !intent) {
          setPhase({ kind: 'error', message: (err as Error).message });
          return;
        }
      }

      // Silent recreate: the bot deeplink still has the buy intent, so we
      // can mint a fresh draft and continue without surfacing the 404.
      try {
        const fresh = await createOrderDraft({
          marketSlug: intent.slug,
          outcomeLabel: intent.outcome,
          side: intent.side,
          amount: intent.amount,
        });
        if (cancelled) return;
        // Keep the URL in sync so a manual reload doesn't hit the dead id.
        const next = new URLSearchParams(search);
        next.set('orderId', fresh.orderId);
        setSearch(next, { replace: true });
        const params = await getOrderParams(fresh.orderId);
        if (!cancelled) setPhase({ kind: 'ready', params });
      } catch (recreateErr) {
        if (!cancelled)
          setPhase({ kind: 'error', message: (recreateErr as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.state, orderId, intent, search, setSearch]);

  const summary = phase.kind === 'ready' ? phase.params.summary : undefined;

  const expiresIn = useMemo(() => {
    if (!summary?.expiresAt) return null;
    const ms = new Date(summary.expiresAt).getTime() - Date.now();
    return ms > 0 ? Math.floor(ms / 1000) : 0;
  }, [summary?.expiresAt]);

  if (!orderId) {
    return (
      <ScreenShell eyebrow="Sign order" title="Order missing">
        <Alert tone="warning">
          <AlertTitle>Missing order ID</AlertTitle>
          <AlertDescription>
            Reopen this screen from the bot — the deeplink didn't carry an
            order reference.
          </AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }
  if (auth.state === 'loading') {
    return (
      <AuthLoadingScreen
        title="Preparing your order"
        description="Authenticating your Telegram session before loading the order."
        shortDescription="Authenticating your session."
      />
    );
  }
  if (auth.state === 'error') {
    return (
      <ScreenShell eyebrow="Sign order" title="Sign order">
        <Alert tone="danger">
          <AlertTitle>Authentication failed</AlertTitle>
          <AlertDescription>{auth.error}</AlertDescription>
        </Alert>
        <div className="mt-4 flex gap-2">
          <Button size="lg" onClick={auth.retry}>
            Tentar novamente
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={() => tryGetTelegram()?.close?.()}
          >
            Voltar ao chat
          </Button>
        </div>
      </ScreenShell>
    );
  }
  if (phase.kind === 'loading') {
    return (
      <AuthLoadingScreen
        title="Loading order"
        description="Fetching the latest typed payload for this Polymarket order."
        shortDescription="Loading order details."
        steps={[
          { label: 'Session', color: 'bg-emerald-500' },
          { label: 'Order', color: 'bg-sky-500', delay: '0.4s' },
          { label: 'Polymarket', color: 'bg-primary', delay: '0.8s' },
        ]}
      />
    );
  }
  if (phase.kind === 'error') {
    return (
      <ScreenShell eyebrow="Sign order" title="Sign order">
        <Alert tone="danger">
          <AlertTitle>Could not load the order</AlertTitle>
          <AlertDescription>{phase.message}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }

  const signing = phase.kind === 'signing';

  async function onSign() {
    if (phase.kind !== 'ready') return;
    const params = phase.params;
    try {
      setPhase({ kind: 'signing' });
      const signer = await getSigner();
      const types = { ...params.typedData.types };
      delete (types as Record<string, unknown>)['EIP712Domain'];
      const sig = await signer.signTypedData(
        params.typedData.domain as never,
        types,
        params.typedData.message,
      );
      let result;
      try {
        result = await submitOrder(orderId!, sig);
      } catch (err) {
        const msg = (err as Error).message;
        if (msg.includes('CLOB L2 credentials')) {
          const creds = await deriveClobCreds(signer);
          await uploadClobCreds(creds);
          try {
            localStorage.setItem(
              `torian.clob.creds.${signer.address.toLowerCase()}`,
              '1',
            );
          } catch {
            /* ignore */
          }
          result = await submitOrder(orderId!, sig);
        } else {
          throw err;
        }
      }
      tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('success');
      setPhase({
        kind: 'submitted',
        orderId: result.id,
        status: result.status,
        errorMessage: result.errorMessage,
      });
    } catch (err) {
      tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('error');
      setPhase({ kind: 'error', message: (err as Error).message });
    }
  }

  if (phase.kind === 'submitted') {
    const failed = phase.status === 'FAILED';
    const needsApprovals =
      failed &&
      typeof phase.errorMessage === 'string' &&
      /needs_approvals/i.test(phase.errorMessage);
    const displayMessage =
      needsApprovals && phase.errorMessage
        ? phase.errorMessage.replace(/^NEEDS_APPROVALS:\s*/i, '')
        : phase.errorMessage;

    return (
      <ScreenShell
        eyebrow={failed ? 'Failed' : 'Submitted'}
        title={failed ? 'Order failed' : 'Order submitted'}
      >
        <Card>
          <CardHeader>
            <CardEyebrow
              className={failed ? 'text-rose-300' : 'text-emerald-300'}
            >
              {failed ? (
                <XCircleIcon weight="fill" className="size-3" />
              ) : (
                <CheckCircleIcon weight="fill" className="size-3" />
              )}
              {phase.status}
            </CardEyebrow>
            <CardTitle>
              {failed ? 'Order could not be placed' : 'Order placed'}
            </CardTitle>
            {failed && displayMessage && (
              <CardDescription>
                {needsApprovals
                  ? 'Trading is not enabled yet. One-time setup approves Polymarket to move pUSD from your deposit wallet.'
                  : displayMessage}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <KeyValue
              label="Order ID"
              value={
                <code className="text-foreground text-[11px]">
                  {short(phase.orderId)}
                </code>
              }
              mono
            />
            <KeyValue
              label="Status"
              value={
                <Badge tone={failed ? 'danger' : 'success'}>{phase.status}</Badge>
              }
            />
          </CardContent>
          <CardFooter>
            {needsApprovals && (
              <Link
                to="/approvals"
                className={buttonVariants({ size: 'lg' })}
              >
                <ShieldCheckIcon weight="bold" className="size-4" />
                Enable trading
              </Link>
            )}
            <Button
              variant={needsApprovals ? 'outline' : 'default'}
              size="lg"
              onClick={() => tryGetTelegram()?.close?.()}
            >
              Back to chat
            </Button>
          </CardFooter>
        </Card>
      </ScreenShell>
    );
  }

  const sideTone =
    summary?.side?.toUpperCase() === 'BUY' ? 'success' : 'warning';

  return (
    <ScreenShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5">
          <SignatureIcon weight="fill" className="size-3" />
          Confirm order
        </span>
      }
      title="Confirm order"
      description="Review the order details below. Signing pushes it to Polymarket's CLOB — gasless and instant."
    >
      <NavBar />
      <Card>
        <CardHeader>
          <CardEyebrow>
            <CoinIcon weight="fill" className="size-3" />
            {summary?.side?.toUpperCase() ?? 'ORDER'}
          </CardEyebrow>
          <CardTitle className="text-base sm:text-lg">
            {summary?.marketQuestion ?? '—'}
          </CardTitle>
          <CardDescription>{summary?.outcomeLabel ?? '—'}</CardDescription>
          {summary?.side && (
            <Badge tone={sideTone} className="self-start">
              {summary.side.toUpperCase()}
            </Badge>
          )}
        </CardHeader>
        <CardContent>
          <KeyValue
            label="Price"
            value={summary ? `${summary.priceCents}¢` : '—'}
          />
          <KeyValue
            label="Stake"
            value={
              summary
                ? fmtUsd(Math.round(summary.totalUsd * 100))
                : '—'
            }
          />
          <KeyValue
            label="Potential payout"
            value={
              summary
                ? fmtUsd(Math.round(summary.potentialPayoutUsd * 100))
                : '—'
            }
          />
          {expiresIn !== null && (
            <KeyValue
              label={
                <span className="inline-flex items-center gap-1.5">
                  <HourglassMediumIcon weight="fill" className="size-3" />
                  Expires
                </span>
              }
              value={
                <span className="inline-flex items-center gap-1.5">
                  <ClockCounterClockwiseIcon weight="fill" className="size-3.5" />
                  {expiresIn}s
                </span>
              }
            />
          )}
          <KeyValue
            label={
              <span className="inline-flex items-center gap-1.5">
                <WalletIcon weight="fill" className="size-3" />
                Wallet
              </span>
            }
            value={short(auth.address)}
            mono
          />
        </CardContent>
        <CardFooter>
          <Button size="lg" onClick={onSign} disabled={signing}>
            {signing ? (
              <SpinnerIcon weight="bold" className="size-4 animate-spin" />
            ) : (
              <SignatureIcon weight="bold" className="size-4" />
            )}
            {signing ? 'Signing…' : 'Sign & submit'}
          </Button>
        </CardFooter>
      </Card>
    </ScreenShell>
  );
}
